import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
// Tooltip in this file is recharts'. antd's is aliased so the two cannot be
// confused: a recharts Tooltip outside a chart renders nothing and fails quietly.
import {
  Row, Col, Card, Statistic, Spin, Table, Select, Input, Space, Tag, DatePicker, Button, Tabs,
  Tooltip as InfoTip,
} from 'antd';
import {
  DollarOutlined,
  ProjectOutlined,
  ThunderboltOutlined,
  SearchOutlined,
  DatabaseOutlined,
  CalendarOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import PageLayout from '../components/layout/PageLayout';
import api from '../libs/api';
import BorrowingBaseTab from './BorrowingBaseTab';
import PpaPipelineTab from './PpaPipelineTab';

/**
 * Longer than the shared default, because this page is slower than the default
 * assumes and failing at thirty seconds is the worse outcome.
 *
 * An all-time load spends about twenty-seven seconds in three billing queries,
 * which sits just under the shared thirty-second ceiling - so the page loaded
 * on a good run and went blank on a slow one, which is the least useful way for
 * something to be broken. The queries are the real problem and are not fixed by
 * waiting longer; this only stops the waiting from being wasted.
 */
const PPA_TIMEOUT = 180000;

const { RangePicker } = DatePicker;

// Fiscal quarter helpers (FY Jul-Jun)
function getFiscalQuarter(d) {
  const m = d.month();
  if (m >= 6 && m <= 8) return { q: 1, start: d.year(), startM: 6, endM: 8 };
  if (m >= 9 && m <= 11) return { q: 2, start: d.year(), startM: 9, endM: 11 };
  if (m >= 0 && m <= 2) return { q: 3, start: d.year(), startM: 0, endM: 2 };
  return { q: 4, start: d.year(), startM: 3, endM: 5 };
}

function getFiscalYearStart(d) {
  return d.month() >= 6
    ? dayjs().year(d.year()).month(6).startOf('month')
    : dayjs().year(d.year() - 1).month(6).startOf('month');
}

const now = dayjs();
const curQ = getFiscalQuarter(now);
const prevQDate = dayjs().month(curQ.startM).startOf('month').subtract(1, 'day');
const prevQ = getFiscalQuarter(prevQDate);
const fyStart = getFiscalYearStart(now);
const fyEnd = dayjs(fyStart).add(1, 'year').subtract(1, 'day').endOf('day');
const lastFyStart = fyStart.subtract(1, 'year');
const lastFyEnd = fyStart.subtract(1, 'day').endOf('day');

const DATE_PRESETS = [
  { label: 'This Month', value: [now.startOf('month'), now.endOf('month')] },
  { label: 'Last Month', value: [now.subtract(1, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')] },
  { label: 'This Quarter', value: [dayjs().year(curQ.start).month(curQ.startM).startOf('month'), dayjs().year(curQ.start).month(curQ.endM).endOf('month')] },
  { label: 'Last Quarter', value: [dayjs().year(prevQ.start).month(prevQ.startM).startOf('month'), dayjs().year(prevQ.start).month(prevQ.endM).endOf('month')] },
  { label: 'Last 6 Months', value: [now.subtract(6, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')] },
  { label: `This Year (FY${fyStart.year()}/${fyStart.year() + 1})`, value: [fyStart, fyEnd] },
  { label: `Last Year (FY${lastFyStart.year()}/${lastFyStart.year() + 1})`, value: [lastFyStart, lastFyEnd] },
  { label: 'All Time', value: [dayjs('2016-01-01'), now.endOf('month')] },
];

const STATUS_COLORS = {
  'Agreement': '#54A2CC',
  'Commissioned': '#05A677',
  'Buy Out': '#f5b759',
  'Default': '#FA5252',
};

const FAIL_BUCKET_COLORS = {
  '0 Fails': '#05A677',
  '1 Fail': '#f5b759',
  '2 Fails': '#FF6F91',
  '3+ Fails': '#FA5252',
};

const PIE_COLORS = ['#54A2CC', '#05A677', '#f5b759', '#FA5252', '#845EC2', '#FF6F91', '#0081CF', '#FFC75F', '#008E9B', '#C34A36'];

const fmt = (v) => {
  if (v == null) return '-';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Number(v).toFixed(0)}`;
};

const dateFmt = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

function groupByField(projects, field) {
  const map = {};
  for (const p of projects) {
    const key = p[field] || 'Unknown';
    if (!map[key]) map[key] = { name: key, value: 0, count: 0 };
    map[key].value += 1;
    map[key].count += 1;
  }
  const result = Object.values(map).sort((a, b) => b.count - a.count);
  const total = result.reduce((s, r) => s + r.count, 0);
  result.forEach(item => { item.pct = total > 0 ? (item.count / total * 100) : 0; });
  return result;
}

function groupByFailBucket(projects) {
  const buckets = { '0 Fails': 0, '1 Fail': 0, '2 Fails': 0, '3+ Fails': 0 };
  for (const p of projects) {
    const fc = p.failedBillCount || 0;
    if (fc === 0) buckets['0 Fails']++;
    else if (fc === 1) buckets['1 Fail']++;
    else if (fc === 2) buckets['2 Fails']++;
    else buckets['3+ Fails']++;
  }
  const total = projects.length;
  return Object.entries(buckets)
    .filter(([, count]) => count > 0)
    .map(([name, count]) => ({ name, count, pct: total > 0 ? count / total * 100 : 0 }));
}

// Cap equipment model list to top N + "Other"
function topN(items, n = 8) {
  if (items.length <= n) return items;
  const top = items.slice(0, n);
  const rest = items.slice(n);
  const otherCount = rest.reduce((s, i) => s + i.count, 0);
  const total = items.reduce((s, i) => s + i.count, 0);
  top.push({ name: 'Other', count: otherCount, pct: total > 0 ? otherCount / total * 100 : 0 });
  return top;
}

const pctFmt = (v) => (v != null ? `${Number(v).toFixed(1)}%` : '-');

function eqCol(typeId, title) {
  return {
    title, key: `eq${typeId}`, width: 180, ellipsis: true, responsive: ['xl'],
    render: (_, r) => {
      const items = (r.equipment || []).filter(e => e.mtType === typeId);
      if (!items.length) return '-';
      return items.map(e => `${e.mtModel}${e.qty > 1 ? ` x${e.qty}` : ''}`).join(', ');
    },
  };
}

const columns = [
  { title: 'Project No', dataIndex: 'projectNo', key: 'projectNo', width: 160, ellipsis: true, responsive: ['md'], fixed: 'left' },
  { title: 'Client', dataIndex: 'clientName', key: 'clientName', ellipsis: true, width: 170, fixed: 'left' },
  {
    title: 'Status', dataIndex: 'status', key: 'status', width: 110,
    render: (v) => <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag>,
  },
  { title: 'State', dataIndex: 'state', key: 'state', width: 60, render: (v) => v || '-' },
  {
    // Ellipsised with the full line on hover: an address is long and this table
    // already carries forty columns, so it earns one column's width, not four.
    title: 'Address',
    dataIndex: 'address',
    key: 'address',
    width: 230,
    ellipsis: true,
    responsive: ['lg'],
    render: (v) => (v ? <InfoTip title={v}>{v}</InfoTip> : '-'),
  },
  { title: 'Agent', dataIndex: 'agentName', key: 'agentName', width: 150, ellipsis: true, responsive: ['lg'] },
  { title: 'Payment', dataIndex: 'paymentType', key: 'paymentType', width: 120, render: (v) => v || '-', responsive: ['lg'] },
  { title: 'Years', dataIndex: 'agreementYears', key: 'agreementYears', width: 60, align: 'right', render: (v) => v || '-' },
  {
    title: 'Sys kW', dataIndex: 'sysKw', key: 'sysKw', align: 'right', width: 80,
    render: (v) => v ? v.toFixed(1) : '-',
    sorter: (a, b) => a.sysKw - b.sysKw,
  },
  {
    title: 'Batt kWh', dataIndex: 'battKwh', key: 'battKwh', align: 'right', width: 85,
    render: (v) => v ? v.toFixed(1) : '-',
    responsive: ['lg'],
  },
  {
    title: 'System Cost', dataIndex: 'systemCost', key: 'systemCost', align: 'right', width: 100,
    render: fmt, sorter: (a, b) => a.systemCost - b.systemCost,
  },
  {
    title: 'Monthly Fee', dataIndex: 'monthlyFee', key: 'monthlyFee', align: 'right', width: 100,
    render: (v) => v > 0 ? `$${v.toFixed(0)}` : '-',
    sorter: (a, b) => a.monthlyFee - b.monthlyFee,
  },
  {
    title: 'Book', dataIndex: 'bookValue', key: 'bookValue', align: 'right', width: 90,
    render: fmt, sorter: (a, b) => a.bookValue - b.bookValue,
  },
  {
    title: 'Deposit', dataIndex: 'deposit', key: 'deposit', align: 'right', width: 90,
    render: fmt, sorter: (a, b) => a.deposit - b.deposit,
  },
  {
    title: 'Paid', dataIndex: 'totalPaid', key: 'totalPaid', align: 'right', width: 90,
    render: fmt, sorter: (a, b) => a.totalPaid - b.totalPaid,
  },
  {
    title: 'Owing', dataIndex: 'owing', key: 'owing', align: 'right', width: 90,
    render: (v) => v > 0 ? <span style={{ color: '#FA5252', fontWeight: 600 }}>{fmt(v)}</span> : (v === 0 ? '-' : fmt(v)),
    sorter: (a, b) => a.owing - b.owing,
  },
  {
    title: 'Owing%', dataIndex: 'owingPct', key: 'owingPct', align: 'right', width: 70,
    render: (v) => v != null && v > 0 ? <span style={{ color: '#FA5252' }}>{pctFmt(v)}</span> : '-',
    sorter: (a, b) => (a.owingPct ?? -1) - (b.owingPct ?? -1),
  },
  // Financial breakdown columns (new calculation approach)
  { title: 'Material', dataIndex: 'material', key: 'material', align: 'right', width: 90, render: fmt, sorter: (a, b) => (a.material || 0) - (b.material || 0), responsive: ['xxl'] },
  { title: 'Extra Material', dataIndex: 'extraMaterial', key: 'extraMaterial', align: 'right', width: 100, render: fmt, sorter: (a, b) => (a.extraMaterial || 0) - (b.extraMaterial || 0), responsive: ['xxl'] },
  { title: 'Total Material', dataIndex: 'totalMaterial', key: 'totalMaterial', align: 'right', width: 100, render: fmt, sorter: (a, b) => (a.totalMaterial || 0) - (b.totalMaterial || 0), responsive: ['xxl'] },
  { title: 'Installation', dataIndex: 'installation', key: 'installation', align: 'right', width: 90, render: fmt, sorter: (a, b) => (a.installation || 0) - (b.installation || 0), responsive: ['xxl'] },
  { title: 'BOS', dataIndex: 'bos', key: 'bos', align: 'right', width: 80, render: fmt, sorter: (a, b) => (a.bos || 0) - (b.bos || 0), responsive: ['xxl'] },
  { title: 'Buffer', dataIndex: 'buffer', key: 'buffer', align: 'right', width: 80, render: fmt, sorter: (a, b) => (a.buffer || 0) - (b.buffer || 0), responsive: ['xxl'] },
  { title: 'Adder', dataIndex: 'adder', key: 'adder', align: 'right', width: 80, render: fmt, sorter: (a, b) => (a.adder || 0) - (b.adder || 0), responsive: ['xxl'] },
  { title: 'Margin', dataIndex: 'margin', key: 'margin', align: 'right', width: 80, render: fmt, sorter: (a, b) => (a.margin || 0) - (b.margin || 0), responsive: ['xxl'] },
  { title: 'STC', dataIndex: 'stcRebate', key: 'stcRebate', align: 'right', width: 80, render: fmt, sorter: (a, b) => (a.stcRebate || 0) - (b.stcRebate || 0), responsive: ['xxl'] },
  { title: 'bSTC', dataIndex: 'batteryStc', key: 'batteryStc', align: 'right', width: 80, render: fmt, sorter: (a, b) => (a.batteryStc || 0) - (b.batteryStc || 0), responsive: ['xxl'] },
  { title: 'Govt Rebate', dataIndex: 'govRebate', key: 'govRebate', align: 'right', width: 90, render: fmt, sorter: (a, b) => (a.govRebate || 0) - (b.govRebate || 0), responsive: ['xxl'] },
  {
    title: () => (
      <div style={{ textAlign: 'right' }}>
        <div>Total Pay to Agent</div>
        <div style={{ fontSize: 10, fontWeight: 400, color: '#999' }}>* = estimated</div>
      </div>
    ),
    key: 'totalPaymentToAgent', align: 'right', width: 120,
    render: (_, r) => {
      if (!r.totalPaymentToAgent) return '-';
      const formatted = fmt(r.totalPaymentToAgent);
      return r.paymentToAgentIsEstimated ? <span>{formatted}*</span> : formatted;
    },
    sorter: (a, b) => (a.totalPaymentToAgent || 0) - (b.totalPaymentToAgent || 0),
    responsive: ['xxl'],
  },
  {
    title: 'Actual Capex',
    key: 'actualCapex',
    align: 'right',
    width: 100,
    /**
     * Not every figure in this column is an actual, and the ones that are not
     * say so.
     *
     * Payment to the agent comes from the RCTI bills where they exist, which is
     * every project commissioned since 2024. Before that the bills predate the
     * system, and the number falls back to a reconstruction from the quote —
     * so it is the design restated, and comparing it to the design tells you
     * nothing. Left unmarked it reads as a cost that happened to land on plan.
     */
    render: (_, r) => {
      const value = fmt(r.actualCapex);
      if (r.paymentToAgentSource !== 'quote') return value;
      return (
        <InfoTip title={'No agent bill for this project — the figure is rebuilt from the quote, '
          + 'so it is the design restated rather than what was spent. RCTI bills cover '
          + 'everything commissioned from 2024 onward.'}>
          <span style={{ color: '#999', borderBottom: '1px dotted #ccc' }}>{value}</span>
        </InfoTip>
      );
    },
    sorter: (a, b) => (a.actualCapex || 0) - (b.actualCapex || 0),
    responsive: ['xxl'],
  },
  {
    title: 'Capex Δ', key: 'capexDiff', align: 'right', width: 90,
    render: (_, r) => {
      if (r.capexDiff == null || Math.abs(r.capexDiff) < 0.01) return '-';
      // Green if under budget (< 0), red if over budget (> 0)
      const color = r.capexDiff < 0 ? '#05A677' : '#FA5252';
      return <span style={{ color }}>{fmt(r.capexDiff)}</span>;
    },
    sorter: (a, b) => (a.capexDiff || 0) - (b.capexDiff || 0),
    responsive: ['xxl'],
  },
  {
    title: 'Multiplier', key: 'multiplier', align: 'right', width: 110,
    render: (_, r) => {
      if (!r.actualMultiplier || !r.designMultiplier) return '-';
      const actual = r.actualMultiplier.toFixed(3);
      const design = r.designMultiplier.toFixed(3);
      // Red only if actual < design - 0.001, green if actual > design, otherwise no color
      const diff = r.actualMultiplier - r.designMultiplier;
      const color = diff < -0.001 ? '#FA5252' : (diff > 0.001 ? '#05A677' : undefined);
      /**
       * A discount shows up here and nowhere else, so it is named here.
       *
       * The design multiplier is struck net of any discount — it comes off the
       * quote that way — while the actual one is the fee over what was really
       * spent. So a discounted project reads below design by roughly the
       * discount, and 118 of the 138 that carry one do. Without the figure
       * beside it that is indistinguishable from a cost overrun, which is the
       * opposite kind of problem.
       */
      const disc = Number(r.discount) || 0;
      return (
        <span style={{ color }}>
          {actual} / {design}
          {disc !== 0 && (
            <InfoTip title={`Design multiplier is net of a $${Math.round(disc).toLocaleString()} discount, `
              + 'so the actual sits below it by about that much. This is a price concession, not a cost overrun.'}>
              <span style={{ color: '#888', fontSize: 11, marginLeft: 4, borderBottom: '1px dotted #ccc' }}>
                −${Math.round(disc).toLocaleString()}
              </span>
            </InfoTip>
          )}
        </span>
      );
    },
    sorter: (a, b) => (a.actualMultiplier || 0) - (b.actualMultiplier || 0),
    responsive: ['xxl'],
  },
  {
    title: 'Latest Bill', key: 'latestBill', align: 'right', width: 130,
    render: (_, r) => {
      if (!r.latestBilAmount) return '-';
      const amt = `$${r.latestBilAmount.toFixed(0)}`;
      const date = r.latestBilDate ? dateFmt(r.latestBilDate) : '';
      return <span>{amt}{date && <span style={{ color: '#999', fontSize: 11, marginLeft: 4 }}>{date}</span>}</span>;
    },
    sorter: (a, b) => (a.latestBilAmount || 0) - (b.latestBilAmount || 0),
    responsive: ['xl'],
  },
  { title: 'Latest Invoice#', dataIndex: 'latestBilInvoiceNo', key: 'latestBilInvoiceNo', width: 120, render: (v) => v || '-', responsive: ['xl'] },
  { title: 'Latest Paid', dataIndex: 'latestPaidDate', key: 'latestPaidDate', width: 110, render: dateFmt, responsive: ['xl'] },
  { title: 'Paid Invoice#', dataIndex: 'latestPaidInvoiceNo', key: 'latestPaidInvoiceNo', width: 120, render: (v) => v || '-', responsive: ['xl'] },
  {
    title: 'Period', key: 'billingPeriod', align: 'right', width: 80,
    render: (_, r) => r.billingN != null ? `${r.billingN}/${r.billingTotal || '?'}` : '-',
    responsive: ['xl'],
  },
  {
    title: 'Fails', dataIndex: 'failedBillCount', key: 'failedBillCount', align: 'right', width: 55,
    render: (v) => v > 0 ? <span style={{ color: '#FA5252', fontWeight: 600 }}>{v}</span> : '0',
    sorter: (a, b) => (a.failedBillCount || 0) - (b.failedBillCount || 0),
  },
  eqCol(1, 'Inverter'),
  eqCol(2, 'Panel'),
  eqCol(3, 'Battery'),
  { title: 'Commissioned', dataIndex: 'commissionDate', key: 'commissionDate', render: dateFmt, width: 110, responsive: ['xl'] },
  { title: 'Created', dataIndex: 'createdAt', key: 'createdAt', render: dateFmt, width: 100, responsive: ['xl'] },
];

// CSV Export function
function exportToCSV(projects) {
  if (!projects || projects.length === 0) {
    alert('No data to export');
    return;
  }

  // Define columns for CSV export (new calculation approach)
  const csvHeaders = [
    'Project No', 'Client Name', 'State', 'Address', 'Agent', 'Payment Type', 'Years',
    'System (kW)', 'Battery (kWh)', 'System Cost', 'Monthly Fee', 'PPA Price',
    'Book Value', 'Deposit', 'Total Paid', 'Owing', 'Owing %',
    'Latest Bill Amount', 'Latest Bill Date', 'Latest Invoice No',
    'Latest Paid Date', 'Latest Paid Invoice No',
    'Billing N', 'Billing Total', 'Failed Bills',
    'Material', 'Extra Material', 'Total Material', 'Installation', 'BOS', 'Buffer', 'Adder', 'Margin',
    'Discount', 'STC', 'bSTC', 'Govt Rebate', 'Total Pay to Agent', 'Actual Capex', 'Capex Source',
    'Capex Diff', 'Capex Diff %',
    'Multiplier Design', 'Multiplier Actual', 'Multiplier Diff', 'Multiplier Diff %',
    'Inverter', 'Panel', 'Battery Equipment',
    'Buyout Price', 'Buyout Date', 'Commission Date', 'Agreement Signed', 'Created', 'Status'
  ];

  // Generate CSV rows
  const csvRows = projects.map(p => {
    const equipment = p.equipment || [];
    const inverter = equipment.filter(e => e.mtType === 1).map(e => `${e.mtModel} (${e.qty})`).join('; ') || '-';
    const panel = equipment.filter(e => e.mtType === 2).map(e => `${e.mtModel} (${e.qty})`).join('; ') || '-';
    const battery = equipment.filter(e => e.mtType === 3).map(e => `${e.mtModel} (${e.qty})`).join('; ') || '-';

    return [
      p.projectNo || '',
      p.clientName || '',
      p.state || '',
      p.address || '',
      p.agentName || '',
      p.paymentType || '',
      p.agreementYears || '',
      p.sysKw || 0,
      p.battKwh || 0,
      p.systemCost || 0,
      p.monthlyFee || 0,
      p.ppaPrice || 0,
      p.bookValue || 0,
      p.deposit || 0,
      p.totalPaid || 0,
      p.owing || 0,
      p.owingPct != null ? p.owingPct.toFixed(1) : '',
      p.latestBilAmount || 0,
      p.latestBilDate ? dateFmt(p.latestBilDate) : '',
      p.latestBilInvoiceNo || '',
      p.latestPaidDate ? dateFmt(p.latestPaidDate) : '',
      p.latestPaidInvoiceNo || '',
      p.billingN || '',
      p.billingTotal || '',
      p.failedBillCount || 0,
      p.material || 0,
      p.extraMaterial || 0,
      p.totalMaterial || 0,
      p.installation || 0,
      p.bos || 0,
      p.buffer || 0,
      p.adder || 0,
      p.margin || 0,
      // A concession on price, not money spent — it is out of Capex Δ for that
      // reason, so it travels as its own column rather than inside a total.
      p.discount || 0,
      p.stcRebate || 0,
      p.batteryStc || 0,
      p.govRebate || 0,
      p.paymentToAgentIsEstimated ? (p.totalPaymentToAgent || 0) + '*' : (p.totalPaymentToAgent || 0),
      p.actualCapex || 0,
      p.paymentToAgentSource === 'rcti' ? 'RCTI bill'
        : p.paymentToAgentSource === 'note' ? 'manual note' : 'rebuilt from quote',
      p.capexDiff || 0,
      p.capexDiffPct != null ? p.capexDiffPct.toFixed(2) : '',
      p.designMultiplier || 0,
      p.actualMultiplier || 0,
      p.multiplierDiff || 0,
      p.multiplierDiffPct != null ? p.multiplierDiffPct.toFixed(2) : '',
      inverter,
      panel,
      battery,
      p.buyoutPrice || '',
      p.buyoutDate ? dateFmt(p.buyoutDate) : '',
      p.commissionDate ? dateFmt(p.commissionDate) : '',
      p.agreementSignedAt ? dateFmt(p.agreementSignedAt) : '',
      p.createdAt ? dateFmt(p.createdAt) : '',
      p.status || ''
    ].map(val => {
      // Escape CSV values
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    });
  });

  // Combine header and rows
  const csvContent = [
    csvHeaders.join(','),
    ...csvRows.map(row => row.join(','))
  ].join('\n');

  // Create download link
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `PPA_Projects_${dayjs().format('YYYY-MM-DD')}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default function PpaSystem() {
  const [activeTab, setActiveTab] = useState('commissioned');
  const [dateRange, setDateRange] = useState([fyStart, fyEnd]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: null, state: null, paymentType: null, agent: null, agreementYears: null, search: '' });
  const [chartFilter, setChartFilter] = useState(null);
  const [pageSize, setPageSize] = useState(20);

  // Payments tab — independent date range
  const [paymentsRange, setPaymentsRange] = useState([now.startOf('month'), now.endOf('month')]);
  const [paymentsData, setPaymentsData] = useState(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  const payReqSeq = useRef(0);
  const payInflight = useRef(null);

  const fetchPayments = useCallback(async (range) => {
    if (!range || !range[0] || !range[1]) return;
    if (payInflight.current) payInflight.current.abort();
    const controller = new AbortController();
    payInflight.current = controller;
    const seq = payReqSeq.current + 1;
    payReqSeq.current = seq;
    setPaymentsLoading(true);
    try {
      const res = await api.get('/api/ppa/payments', {
        params: { start: range[0].format('YYYY-MM-DD'), end: range[1].format('YYYY-MM-DD') },
        signal: controller.signal,
        timeout: PPA_TIMEOUT,
      });
      if (seq !== payReqSeq.current) return;
      setPaymentsData(res.data.data);
    } catch { /* aborted, or handled by the interceptor */ }
    if (seq === payReqSeq.current) setPaymentsLoading(false);
  }, []);

  const paymentsRangeKey = paymentsRange ? `${paymentsRange[0]?.format('YYYY-MM-DD')}_${paymentsRange[1]?.format('YYYY-MM-DD')}` : '';
  useEffect(() => {
    if (activeTab === 'payments' && paymentsRange) fetchPayments(paymentsRange);
  }, [activeTab, paymentsRangeKey, fetchPayments]);

  /**
   * Only the newest request may write to the page.
   *
   * This endpoint takes twenty-five seconds and more — the all-time range
   * returns 4.2MB after forty-seven — so changing the date twice leaves two
   * requests in flight, and without a guard whichever finishes last wins. The
   * server logs caught two of them landing 2ms apart for ranges ending
   * 2026-08-30 and 2026-08-31, which is exactly how somebody picks a date and
   * watches the scorecards keep the old numbers.
   *
   * The stale one also used to call setLoading(false) on the way past, so the
   * spinner stopped while the real answer was still coming and the page looked
   * settled on the wrong figures.
   *
   * The superseded request is aborted as well as ignored: there is no reason to
   * leave the server twenty-five seconds of work for an answer nobody will read.
   */
  const reqSeq = useRef(0);
  const inflight = useRef(null);

  const fetchData = useCallback(async (range, f) => {
    if (!range || !range[0] || !range[1]) return;

    if (inflight.current) inflight.current.abort();
    const controller = new AbortController();
    inflight.current = controller;
    const seq = reqSeq.current + 1;
    reqSeq.current = seq;

    setLoading(true);
    try {
      const params = {
        start: range[0].format('YYYY-MM-DD'),
        end: range[1].format('YYYY-MM-DD'),
      };
      if (f?.status) params.status = f.status;
      if (f?.state) params.state = f.state;
      if (f?.paymentType) params.paymentType = f.paymentType;
      if (f?.agent) params.agent = f.agent;
      if (f?.agreementYears) params.agreementYears = f.agreementYears;
      if (f?.search) params.search = f.search;
      const res = await api.get('/api/ppa/summary', {
        params, signal: controller.signal, timeout: PPA_TIMEOUT,
      });
      if (seq !== reqSeq.current) return;          // a newer request has been sent
      setData(res.data.data);
    } catch { /* aborted, or handled by the interceptor */ }
    // Only the newest request owns the spinner.
    if (seq === reqSeq.current) setLoading(false);
  }, []);

  // Use date string as dependency to properly detect changes
  const dateRangeKey = dateRange ? `${dateRange[0]?.format('YYYY-MM-DD')}_${dateRange[1]?.format('YYYY-MM-DD')}` : '';
  useEffect(() => {
    if (dateRange) {
      fetchData(dateRange, filters);
    }
  }, [dateRangeKey, filters, fetchData]);

  const handleFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value || null }));
  };

  // Clear chart filter when backend data changes
  useEffect(() => { setChartFilter(null); }, [data]);

  const handleChartClick = (type, value, label) => {
    setChartFilter(prev => (prev?.type === type && prev?.value === value) ? null : { type, value, label });
  };

  const s = data?.stats || {};
  const projects = data?.projects || [];
  const opts = data?.filterOptions || {};

  // Client-side chart filter
  const filteredProjects = useMemo(() => {
    if (!chartFilter) return projects;
    const { type, value } = chartFilter;
    switch (type) {
      case 'status': return projects.filter(p => p.status === value);
      case 'state': return projects.filter(p => (p.state || 'Unknown') === value);
      case 'paymentType': return projects.filter(p => (p.paymentType || 'Unknown') === value);
      case 'agentName': return projects.filter(p => (p.agentName || 'Unknown') === value);
      case 'failBucket': return projects.filter(p => {
        const fc = p.failedBillCount || 0;
        if (value === '0 Fails') return fc === 0;
        if (value === '1 Fail') return fc === 1;
        if (value === '2 Fails') return fc === 2;
        if (value === '3+ Fails') return fc >= 3;
        return true;
      });
      default: return projects;
    }
  }, [projects, chartFilter]);

  // Recompute stats from filtered projects (or use server stats if no chart filter).
  // Row 1 (cohort) is recomputed from filtered projects; Row 2 (in-period activity +
  // current snapshot) is always pass-through from server — it can't be filtered
  // client-side because Row-2 stats use ALL active PPA projects, not the cohort list.
  const fs = useMemo(() => {
    if (!chartFilter) return s;
    const fp = filteredProjects;
    const fees = fp.map(p => p.monthlyFee).filter(v => v > 0);
    return {
      projectCount: fp.length,
      totalSysKw: fp.reduce((sum, p) => sum + (p.sysKw || 0), 0),
      totalBattKwh: fp.reduce((sum, p) => sum + (p.battKwh || 0), 0),
      totalSystemCost: fp.reduce((sum, p) => sum + (p.systemCost || 0), 0),
      avgMonthlyFee: fees.length ? fees.reduce((a, b) => a + b, 0) / fees.length : 0,
      totalBook: fp.reduce((sum, p) => sum + (p.bookValue || 0), 0),
      // Invoiced, collected, owing and prepaid were passed through here and
      // never rendered — those cards are on the Payments tab, fed by
      // /api/ppa/payments. Dropping them lets the summary endpoint stop
      // computing them, which is where its 28 seconds went.
    };
  }, [chartFilter, filteredProjects, s]);

  // Pie chart data
  const pieByStatus = useMemo(() => groupByField(projects, 'status'), [projects]);
  const pieByState = useMemo(() => groupByField(projects, 'state'), [projects]);
  const pieByPayment = useMemo(() => groupByField(projects, 'paymentType'), [projects]);
  const pieByFailBucket = useMemo(() => groupByFailBucket(projects), [projects]);

  // Equipment pie data (computed from filteredProjects so it responds to filters)
  const equipmentPies = useMemo(() => {
    const agg = { 1: {}, 2: {}, 3: {} };
    for (const p of filteredProjects) {
      for (const eq of (p.equipment || [])) {
        if (!agg[eq.mtType]) continue;
        agg[eq.mtType][eq.mtModel] = (agg[eq.mtType][eq.mtModel] || 0) + eq.qty;
      }
    }
    const result = {};
    const typeNames = { 1: 'Inverter', 2: 'Panel', 3: 'Battery' };
    for (const [type, models] of Object.entries(agg)) {
      const items = Object.entries(models)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
      const total = items.reduce((sum, i) => sum + i.count, 0);
      items.forEach(i => { i.pct = total > 0 ? (i.count / total) * 100 : 0; });
      result[typeNames[type]] = topN(items);
    }
    return result;
  }, [filteredProjects]);

  const tableTitle = chartFilter
    ? `${filteredProjects.length} of ${s.projectCount || 0} Projects — ${chartFilter.label}`
    : `${s.projectCount || 0} Projects`;

  const renderPie = (pieData, title, filterType, opts = {}) => {
    const { colProps, colorMap } = opts;
    const cols = colProps || { xs: 24, md: 8 };
    return (
      <Col {...cols}>
        <Card size="small" title={title} style={{ marginTop: 16 }}>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="count"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, count, pct }) => {
                  const short = name.length > 22 ? name.substring(0, 20) + '..' : name;
                  return `${short} (${count}) ${pct.toFixed(0)}%`;
                }}
                fontSize={10}
                onClick={filterType ? (entry) => handleChartClick(filterType, entry.name, `${title}: ${entry.name}`) : undefined}
                style={filterType ? { cursor: 'pointer' } : undefined}
              >
                {pieData.map((entry, i) => {
                  const color = colorMap?.[entry.name] || PIE_COLORS[i % PIE_COLORS.length];
                  return (
                    <Cell
                      key={i}
                      fill={color}
                      opacity={filterType && chartFilter?.type === filterType && chartFilter?.value !== entry.name ? 0.3 : 1}
                    />
                  );
                })}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </Col>
    );
  };

  const commissionedContent = (
    <>
      <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <Space wrap size="middle" align="center">
          <CalendarOutlined style={{ color: '#54A2CC' }} />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            format="DD MMM YYYY"
            size="small"
            allowClear={false}
            presets={DATE_PRESETS}
          />
        </Space>
      </Card>
      <Spin spinning={loading}>
        {/* KPI Score Cards - Row 1 */}
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Projects"
                value={fs.projectCount || 0}
                prefix={<ProjectOutlined style={{ color: '#54A2CC' }} />}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                {fs.totalSysKw ? `${(fs.totalSysKw / 1000).toFixed(1)} MW capacity` : ''}
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Battery"
                value={fs.totalBattKwh ? `${(fs.totalBattKwh / 1000).toFixed(1)}` : '0'}
                suffix="MWh"
                prefix={<DatabaseOutlined style={{ color: '#845EC2' }} />}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                {fs.totalBattKwh && fs.projectCount ? `${Math.round(fs.totalBattKwh / fs.projectCount * 10) / 10} kWh avg` : ''}
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="System Cost"
                value={fs.totalSystemCost}
                formatter={(v) => fmt(v)}
                prefix={<DollarOutlined style={{ color: '#54A2CC' }} />}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                {fs.projectCount ? `Avg ${fmt(fs.totalSystemCost / fs.projectCount)}` : ''}
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Book Value"
                value={fs.totalBook}
                formatter={(v) => fmt(v)}
                prefix={<DollarOutlined style={{ color: '#0081CF' }} />}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                {fs.avgMonthlyFee ? `Avg fee $${Math.round(fs.avgMonthlyFee)}/mo` : ''}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card style={{ marginTop: 16 }} size="small">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <Space wrap size="middle">
              <Select
                placeholder="Status"
                allowClear
                mode="multiple"
                style={{ minWidth: 180 }}
                value={filters.status ? filters.status.split(',') : []}
                onChange={(v) => handleFilter('status', v.length ? v.join(',') : null)}
                options={[
                  { label: 'Agreement', value: '3' },
                  { label: 'Commissioned', value: '5' },
                  { label: 'Buy Out', value: '7' },
                  { label: 'Default', value: '8' },
                ]}
                maxTagCount={2}
              />
              <Select
                placeholder="State"
                allowClear
                style={{ width: 100 }}
                value={filters.state}
                onChange={(v) => handleFilter('state', v)}
                options={opts.states?.map(st => ({ label: st, value: st })) || []}
              />
              <Select
                placeholder="Payment Type"
                allowClear
                style={{ width: 160 }}
                value={filters.paymentType}
                onChange={(v) => handleFilter('paymentType', v)}
                options={opts.paymentTypes?.map(pt => ({ label: pt, value: pt })) || []}
              />
              <Select
                placeholder="Agent"
                allowClear
                showSearch
                style={{ width: 180 }}
                value={filters.agent}
                onChange={(v) => handleFilter('agent', v)}
                options={opts.agents?.map(ag => ({ label: ag, value: ag })) || []}
              />
              <Select
                placeholder="Years"
                allowClear
                style={{ width: 80 }}
                value={filters.agreementYears}
                onChange={(v) => handleFilter('agreementYears', v)}
                options={opts.agreementYears?.map(y => ({ label: `${y}y`, value: String(y) })) || []}
              />
              <Input
                placeholder="Search name or project"
                allowClear
                prefix={<SearchOutlined />}
                style={{ width: 200 }}
                value={filters.search}
                onChange={(e) => handleFilter('search', e.target.value)}
              />
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => exportToCSV(filteredProjects)}
              >
                Export CSV
              </Button>
            </Space>
            {chartFilter && (
              <Tag closable onClose={() => setChartFilter(null)} color="blue" style={{ fontSize: 13, margin: 0 }}>
                {chartFilter.label}
              </Tag>
            )}
          </div>
        </Card>

        {/* Pie Charts — Row 1: Status, State, Payment Type, Billing Fails */}
        {projects.length > 0 && (
          <>
            <Row gutter={[16, 0]}>
              {renderPie(pieByStatus, 'Projects by Status', 'status', { colProps: { xs: 24, md: 12, lg: 6 }, colorMap: STATUS_COLORS })}
              {renderPie(pieByState, 'Projects by State', 'state', { colProps: { xs: 24, md: 12, lg: 6 } })}
              {renderPie(pieByPayment, 'Projects by Payment Type', 'paymentType', { colProps: { xs: 24, md: 12, lg: 6 } })}
              {renderPie(pieByFailBucket, 'Bill Fail Count', 'failBucket', { colProps: { xs: 24, md: 12, lg: 6 }, colorMap: FAIL_BUCKET_COLORS })}
            </Row>
            {/* Pie Charts — Row 2: Equipment (Inverter, Panel, Battery) */}
            <Row gutter={[16, 0]}>
              {renderPie(equipmentPies.Inverter || [], 'Inverters', null, { colProps: { xs: 24, md: 8 } })}
              {renderPie(equipmentPies.Panel || [], 'Panels', null, { colProps: { xs: 24, md: 8 } })}
              {renderPie(equipmentPies.Battery || [], 'Batteries', null, { colProps: { xs: 24, md: 8 } })}
            </Row>
          </>
        )}

        {/* Projects Table */}
        <Card style={{ marginTop: 16 }} title={tableTitle}>
          <Table
            dataSource={filteredProjects}
            columns={columns}
            rowKey="projectId"
            pagination={{
              pageSize,
              showSizeChanger: true,
              showTotal: (t) => `${t} projects`,
              onChange: (page, size) => setPageSize(size),
              onShowSizeChange: (current, size) => setPageSize(size),
            }}
            size="small"
            scroll={{ x: 4850 }}
          />
        </Card>
      </Spin>
    </>
  );

  // ---- Payments tab content ----
  const paymentsStats = paymentsData?.stats || {};
  const paymentsByType = paymentsData?.byPaymentType || [];
  const paymentsByStatus = paymentsData?.byStatus || [];
  const paymentsOwingProjects = paymentsData?.owingProjects || [];

  const STATUS_PIE_COLORS = { Paid: '#05A677', Partial: '#f5b759', Unpaid: '#FA5252' };
  const TYPE_PIE_COLORS = ['#54A2CC', '#05A677', '#f5b759', '#845EC2', '#FA5252', '#8B4513'];

  const paymentsContent = (
    <>
      <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <Space wrap size="middle" align="center">
          <CalendarOutlined style={{ color: '#54A2CC' }} />
          <span style={{ fontSize: 12, color: '#666' }}>Bills with billing date in:</span>
          <RangePicker
            value={paymentsRange}
            onChange={(d) => setPaymentsRange(d)}
            format="DD MMM YYYY"
            size="small"
            allowClear={false}
            presets={DATE_PRESETS}
          />
        </Space>
      </Card>
      <Spin spinning={paymentsLoading}>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic title="Invoice Sent" value={paymentsStats.totalInvoiced || 0}
                formatter={(v) => fmt(v)} prefix={<DollarOutlined style={{ color: '#0081CF' }} />} />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                {paymentsStats.invoiceCount ? `${paymentsStats.invoiceCount} invoices in period` : '-'}
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic title="Collected" value={paymentsStats.totalCollected || 0}
                formatter={(v) => fmt(v)} prefix={<DollarOutlined style={{ color: '#05A677' }} />}
                valueStyle={{ color: '#05A677' }} />
              <div style={{ fontSize: 12, color: '#05A677', marginTop: 4 }}>
                {paymentsStats.totalInvoiced > 0
                  ? `${((paymentsStats.totalCollected || 0) / paymentsStats.totalInvoiced * 100).toFixed(1)}% of invoiced`
                  : '-'}
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic title="Owing on these bills" value={paymentsStats.totalOwing || 0}
                formatter={(v) => fmt(v)} prefix={<DollarOutlined style={{ color: '#FA5252' }} />}
                valueStyle={{ color: paymentsStats.totalOwing > 0 ? '#FA5252' : undefined }} />
              <div style={{ fontSize: 12, color: '#FA5252', marginTop: 4 }}>
                {paymentsStats.unpaidCount || paymentsStats.partialCount
                  ? `${paymentsStats.unpaidCount || 0} unpaid · ${paymentsStats.partialCount || 0} partial`
                  : '-'}
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic title="Prepaid on these bills" value={paymentsStats.totalPrepaid || 0}
                formatter={(v) => fmt(v)} prefix={<DollarOutlined style={{ color: '#05A677' }} />} />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                {paymentsStats.paidCount ? `${paymentsStats.paidCount} fully paid` : '-'}
              </div>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <Card title="By Payment Type" size="small">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={paymentsByType.map((d, i) => ({ name: d.paymentTypeName, value: d.totalInvoiced, count: d.invoiceCount, color: TYPE_PIE_COLORS[i % TYPE_PIE_COLORS.length] }))}
                       dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100}
                       label={(entry) => `${entry.name}: ${fmt(entry.value)}`}>
                    {paymentsByType.map((_, i) => <Cell key={i} fill={TYPE_PIE_COLORS[i % TYPE_PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v, n, props) => [`${fmt(v)} (${props.payload.count} invoices)`, props.payload.name]} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="By Payment Status" size="small">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={paymentsByStatus.map(d => ({ name: d.status, value: d.totalInvoiced, count: d.invoiceCount }))}
                       dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100}
                       label={(entry) => `${entry.name}: ${fmt(entry.value)}`}>
                    {paymentsByStatus.map((d, i) => <Cell key={i} fill={STATUS_PIE_COLORS[d.status] || '#999'} />)}
                  </Pie>
                  <Tooltip formatter={(v, n, props) => [`${fmt(v)} (${props.payload.count} invoices)`, props.payload.name]} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        <Card
          title={`Owing Projects (on bills in this period) — ${paymentsOwingProjects.length} total`}
          size="small"
          style={{ marginTop: 16 }}
        >
          <Table
            size="small"
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100', '200'],
              showTotal: (t) => `${t} owing projects`,
            }}
            scroll={{ x: 1100 }}
            dataSource={paymentsOwingProjects.map((r, i) => ({ ...r, key: r.projectId || i }))}
            columns={[
              { title: 'Project No.', dataIndex: 'projectNo', width: 110, fixed: 'left' },
              { title: 'Client', dataIndex: 'clientName', ellipsis: true, width: 200 },
              {
                title: 'Status', dataIndex: 'status', width: 110,
                render: (v) => <Tag color={v === 'Default' ? 'red' : v === 'Buy Out' ? 'orange' : v === 'Commissioned' ? 'green' : 'blue'}>{v}</Tag>,
              },
              { title: 'Bills', dataIndex: 'invoiceCount', align: 'right', width: 70,
                sorter: (a, b) => (a.invoiceCount || 0) - (b.invoiceCount || 0) },
              { title: 'Invoiced', dataIndex: 'totalInvoiced', align: 'right', width: 120,
                render: (v) => fmt(v),
                sorter: (a, b) => (a.totalInvoiced || 0) - (b.totalInvoiced || 0) },
              { title: 'Paid', dataIndex: 'totalPaid', align: 'right', width: 120,
                render: (v) => <span style={{ color: '#05A677' }}>{fmt(v)}</span>,
                sorter: (a, b) => (a.totalPaid || 0) - (b.totalPaid || 0) },
              { title: 'Owing', dataIndex: 'owingAmount', align: 'right', width: 120,
                render: (v) => <span style={{ color: '#FA5252', fontWeight: 600 }}>{fmt(v)}</span>,
                sorter: (a, b) => (a.owingAmount || 0) - (b.owingAmount || 0),
                defaultSortOrder: 'descend' },
              {
                title: 'Bill date range', width: 200,
                render: (_, r) => {
                  if (!r.earliestBill && !r.latestBill) return '-';
                  const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' }) : '?';
                  return <span style={{ fontSize: 11, color: '#666' }}>{fmtD(r.earliestBill)} → {fmtD(r.latestBill)}</span>;
                },
              },
            ]}
          />
        </Card>
      </Spin>
    </>
  );

  return (
    <PageLayout>
      <div className="page-header">
        <h1>PPA System</h1>
        <p>PPA / Monthly Payment project tracking</p>
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'commissioned', label: 'Commissioned Systems', children: commissionedContent },
          // Next to Commissioned rather than last: the two are the same book
          // either side of the commission date, and until now only one half
          // of it had anywhere to appear.
          { key: 'pipeline', label: 'Pipeline', children: <PpaPipelineTab /> },
          { key: 'payments', label: 'Payments', children: paymentsContent },
          { key: 'borrowingBase', label: 'Borrowing Base', children: <BorrowingBaseTab /> },
        ]}
      />
    </PageLayout>
  );
}
